/**
 * RaumbuchListPanel — Kompakte Raumliste für das ContentPanel (Sidebar).
 * Zeigt Zusammenfassung (Anzahl Räume, Gesamtfläche) + scrollbare Liste.
 * Design: Mockup "Raumbuch"-Tab im Gebäude-Panel.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'

// ── Potree Volume duck-type ──────────────────────────────────
interface PotreeVolume {
  id: number
  label: string
  name: string
  profile: { x: number; y: number }[]
  scale: { x: number; y: number; z: number }
  _floorId: string | null
  _usage: string
  _volumeType: string
  _raumbuch?: Record<string, string>
  activate?: () => void
}

// ── Geometrie ────────────────────────────────────────────────
function computeArea(profile: { x: number; y: number }[], sx: number, sy: number): number {
  let a = 0
  const n = profile.length
  for (let i = 0; i < n; i++) {
    const cur = profile[i], next = profile[(i + 1) % n]
    a += cur.x * next.y - next.x * cur.y
  }
  return Math.abs(a / 2) * sx * sy
}

function floorLabel(floorId: string | null): string {
  return floorId || '—'
}

// ── Row-Typ ──────────────────────────────────────────────────
interface RoomRow {
  id: number
  nr: string
  name: string
  area: number
  floor: string
}

// ── Hook: Potree Volumes als kompakte Rows ───────────────────
function useRoomRows(): RoomRow[] {
  const [rows, setRows] = useState<RoomRow[]>([])

  const refresh = useCallback(() => {
    const vols: PotreeVolume[] = (window as any)._clipExtrudedVolumes || []
    const roomVols = vols.filter(v => v._volumeType === 'room' && !(v as any)._isClipProxy)
    setRows(roomVols.map(vol => {
      const rb = vol._raumbuch || {}
      return {
        id: vol.id,
        nr: rb.nr || vol.label,
        name: vol.name || '',
        area: computeArea(vol.profile, Math.abs(vol.scale.x), Math.abs(vol.scale.y)),
        floor: floorLabel(vol._floorId),
      }
    }))
  }, [])

  useEffect(() => {
    refresh()
    const timer = setInterval(refresh, 2000)
    const handler = () => refresh()
    window.addEventListener('orto:roomsChanged', handler)
    return () => {
      clearInterval(timer)
      window.removeEventListener('orto:roomsChanged', handler)
    }
  }, [refresh])

  return rows
}

// ── Formatierung ─────────────────────────────────────────────
function fmtArea(v: number): string {
  return v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// ── Hauptkomponente ──────────────────────────────────────────
export function RaumbuchListPanel() {
  const rows = useRoomRows()
  const [filterFloor, setFilterFloor] = useState<string | null>(null)

  const floors = useMemo(() => {
    const set = new Set(rows.map(r => r.floor))
    return [...set].sort()
  }, [rows])

  const filtered = filterFloor ? rows.filter(r => r.floor === filterFloor) : rows
  const totalArea = filtered.reduce((sum, r) => sum + r.area, 0)

  const handleRowClick = useCallback((id: number) => {
    const vols: PotreeVolume[] = (window as any)._clipExtrudedVolumes || []
    const vol = vols.find(v => v.id === id)
    if (!vol) return
    if (typeof (window as any).activateVolume === 'function') {
      (window as any).activateVolume(vol)
    }
    window.dispatchEvent(new CustomEvent('orto:flyToVolume', { detail: { volumeId: id } }))
  }, [])

  return (
    <div className="rb-list">
      {/* Summary bar */}
      <div className="rb-list__summary">
        <span className="rb-list__badge">{filtered.length} Räume</span>
        <span className="rb-list__badge">Σ {fmtArea(totalArea)} m²</span>
        <div className="rb-list__spacer" />
        <div className="rb-list__filter">
          {filterFloor ? (
            <button
              className="rb-list__filter-btn rb-list__filter-btn--active"
              onClick={() => setFilterFloor(null)}
            >
              {filterFloor} ×
            </button>
          ) : (
            <div className="rb-list__filter-group">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
              </svg>
              <select
                className="rb-list__filter-select"
                value=""
                onChange={e => setFilterFloor(e.target.value || null)}
              >
                <option value="">Filter</option>
                {floors.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Room list */}
      <div className="rb-list__body">
        {filtered.map(row => (
          <button
            key={row.id}
            className="rb-list__row"
            onClick={() => handleRowClick(row.id)}
          >
            <span className="rb-list__nr">{row.nr}</span>
            <span className="rb-list__name">{row.name || '—'}</span>
            <span className="rb-list__area">{fmtArea(row.area)}</span>
            <span className="rb-list__unit">m²</span>
            <span className="rb-list__floor">{row.floor}</span>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="rb-list__empty">Keine Räume vorhanden</div>
        )}
      </div>
    </div>
  )
}
